import { IOpporunitiesContext } from 'pages/Opportunities/context/OpportunitiesContext.types';
import { ENDPOINTS } from 'shared/constants/ENDPOINTS';
import { useAxios } from 'shared/hooks/useAxios';
import {
  IApplyToOpportunityPayload,
  IOpportunities,
  IOpportunitiesPublicAggregates,
  IOpportunitiesRemoveProjectResponse,
  IOpportunitiesTabAggregates,
  IProjectRoles,
  ISpecificProjectResponse,
  ISpecificRoleResponse,
} from 'shared/interfaces/utils/IOpportunities';
import {
  GET_OPPORTUNITIES,
  GET_PROJECT,
  GET_PROJECT_ROLES,
  GET_PROJECT_ROLES_COMPLETE,
  GET_PROJECT_WITH_ROLES,
  GET_PUBLIC_PROJECT,
  GET_PUBLIC_ROLE,
  GET_ROLE,
} from 'shared/queries/opportunities.queries';
import { authToken } from 'shared/utils/authToken';
import { boolean } from 'yup';

const { POST, GET } = useAxios();
const opportunitiesDao = () => {
  const { getAuthToken } = authToken();
  const getOpportunities = async (payload?: any) => {
    const response = await POST<IOpportunities>({
      url: `${ENDPOINTS.GRAPHQL}`,
      data: {
        query: GET_OPPORTUNITIES,
        variables: {
          first: 2,
          ...payload,
        },
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const getProjectRoles = async (payload: any, complete: boolean, showAll?: boolean) => {
    const response = await POST<IProjectRoles>({
      url: `${ENDPOINTS.GRAPHQL}`,
      data: {
        query: complete ? GET_PROJECT_ROLES_COMPLETE : GET_PROJECT_ROLES,
        variables: {
          ...(!showAll && { first: 2 }),
          ...payload,
        },
      },
    });
    return response.data;
  };

  const getProjectInfo = async (id: string, isPublic?: boolean, withRoles?: boolean) => {
    const response = await POST<ISpecificProjectResponse>({
      url: `${ENDPOINTS.GRAPHQL}`,
      data: {
        query: withRoles ? GET_PROJECT_WITH_ROLES : isPublic ? GET_PUBLIC_PROJECT : GET_PROJECT,
        variables: {
          [isPublic ? 'project_slug' : 'id']: id,
        },
      },
    });
    return response.data;
  };

  const getRoleInfo = async (id: string, isPublic?: boolean) => {
    const response = await POST<ISpecificRoleResponse>({
      url: `${ENDPOINTS.GRAPHQL}`,
      data: {
        query: isPublic ? GET_PUBLIC_ROLE : GET_ROLE,
        variables: {
          ...(isPublic ? { slug: id } : { id }),
        },
      },
    });
    return response.data;
  };

  const getTabAggregates = async (filters: IOpporunitiesContext['filters']) => {
    const removedEmptyValues = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v != null && v !== '' && v !== undefined),
    );
    delete removedEmptyValues.project_ids;
    delete removedEmptyValues.role_ids;

    const response = await GET<IOpportunitiesTabAggregates>({
      url: `${ENDPOINTS.OPPORTUNITY_TAB_AGGREGATES}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: { ...removedEmptyValues, countries: [removedEmptyValues.countries] },

      //params remove all the empty values
    });
    return response.data;
  };

  const getPublicAggregates = async (filters: Partial<IOpporunitiesContext['filters']>) => {
    const removedEmptyValues = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''));
    delete removedEmptyValues.project_ids;
    delete removedEmptyValues.role_ids;

    const response = await GET<IOpportunitiesPublicAggregates>({
      url: `${ENDPOINTS.OPPORTUNITY_PUBLIC_AGGREGATES}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: { ...removedEmptyValues, countries: [removedEmptyValues.countries] },

      //params remove all the empty values
    });
    return response.data;
  };

  const removeProjects = async (ids: string[]) => {
    const response = await POST<IOpportunitiesRemoveProjectResponse>({
      url: `${ENDPOINTS.OPPORTUNITY_REMOVE_PROJECT}`,
      data: {
        project_ids: ids,
      },
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const applyToRole = async (payload: IApplyToOpportunityPayload) => {
    const response = await POST<{ message: string }>({
      url: `${ENDPOINTS.OPPORTUNITY_APPLY}`,
      data: payload,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });
    return response.data;
  };

  const checkAudition = async (casting_role_id: string) => {
    const response = await GET<{ data: boolean }>({
      url: `${ENDPOINTS.AUDITION_CHECKER}`,
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      params: {
        casting_role_id,
      },

      //params remove all the empty values
    });
    return response.data;
  };

  return {
    checkAudition,
    getProjectRoles,
    getPublicAggregates,
    applyToRole,
    removeProjects,
    getTabAggregates,
    getProjectInfo,
    getOpportunities,
    getRoleInfo,
  };
};
export default opportunitiesDao;
